import '../shared/polyfill';
import React, { useState, useEffect, useMemo } from 'react';
import App from 'next/app';
import { useRouter } from 'next/router';
import {
  ThemeProvider,
  type Theme,
  StyledEngineProvider,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Hydrate } from 'react-query/hydration';
import { ReactQueryDevtools } from 'react-query/devtools';
import Bugsnag from '@bugsnag/js';
import Head from 'next/head';
import type { AppProps } from 'next/app';
import { I18nProvider } from '@procore/core-react';
import LaunchDarkly from '../components/LaunchDarkly/LaunchDarkly';
import theme from '../styles/theme';
import '../styles/responsive-fonts.css';
import '../styles/global.css';
import AppContext from '../components/AppContext/AppContext';
import isLoggedIn from '../data/monolith/is-logged-in';
import MarketingTracking from '../components/Marketing/MarketingTracking';
import enTranslations from '../locales/en.json';
import { setAnalyticsUser } from '../components/Analytics/utilities';
import { init as initializeBugsnag } from '../lib/bugsnag/bugsnag';
import '../ignoreNoisyWarnings';
import Otel from '../components/Otel/Otel';
import { useMFETranslations } from '@procore/cdn-translations';
import { supportedLocales } from '../helpers/setup-i18next';
import { type TmsSupportedLocale } from '@procore/globalization-toolkit';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

initializeBugsnag();

MyApp.getInitialProps = async (appContext: any) => {
  return {
    ...App.getInitialProps(appContext),
    pageProps: {
      children: null,
    },
  };
};

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

export const supportedLocaleMapping: Record<
  (typeof supportedLocales)[number],
  TmsSupportedLocale
> = {
  'en-US': 'en',
  'en-CA': 'en-CA',
};

function AppProvider({ isUserLoggedIn, loginInformationId, locale, children }) {
  const applocale = (supportedLocaleMapping[locale] ?? 'en') as Parameters<
    typeof I18nProvider
  >[0]['locale'];

  const translationsCDN = useMFETranslations(
    {
      type: 'file',
      locale: applocale,
      absolute_file_path: (cdnLocale) =>
        `construction_network_front_end/src/locales/${cdnLocale}.json`,
    },
    {
      en: enTranslations,
    },
    {
      oldTranslations: {},
      enableCDN: true,
    }
  );

  const userAuthMemo = useMemo(
    () => ({ isUserLoggedIn, loginInformationId }),
    [isUserLoggedIn, loginInformationId]
  );

  return (
    <I18nProvider
      translations={translationsCDN.translations}
      locale={applocale}
      enableCDN
    >
      <AppContext.Provider value={userAuthMemo}>{children}</AppContext.Provider>
    </I18nProvider>
  );
}

const Main = ({
  Component,
  pageProps,
  isUserLoggedIn,
  loginInformationId,
  queryClient,
  locale,
}): JSX.Element => {
  return (
    <StyledEngineProvider injectFirst>
      <AppProvider
        isUserLoggedIn={isUserLoggedIn}
        loginInformationId={loginInformationId}
        locale={locale}
      >
        <Head>
          <title>Procore Construction Network</title>
          <meta
            name="description"
            content="Connect with thousands of commercial contractors on Procore. Search, find and connect with subcontractors & general contractors on one platform, Procore."
          />
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width"
            key="viewport-meta"
          />
        </Head>
        <MarketingTracking />
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
              <ThemeProvider theme={theme}>
                {/* CSS Reset */}
                <CssBaseline />
                <LaunchDarkly>
                  <Otel />
                  <Component {...pageProps} />
                </LaunchDarkly>
              </ThemeProvider>
              <ReactQueryDevtools initialIsOpen={false} />
            </Hydrate>
          </QueryClientProvider>
        </ErrorBoundary>
      </AppProvider>
    </StyledEngineProvider>
  );
};

/*
 * Controls the initialization of pages.
 * Docs: https://nextjs.org/docs/advanced-features/custom-app
 */
export default function MyApp({
  Component,
  pageProps,
}: AppProps<{ dehydratedState: unknown }>) {
  const { locale } = useRouter();
  const [queryClient] = useState(() => new QueryClient());
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(undefined);
  const [loginInformationId, setLoginInformationId] = useState(null);

  useEffect(() => {
    // Remove the server-side injected CSS
    const jssStyles = document.querySelector('#jss-server-side');
    /* istanbul ignore next */
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    isLoggedIn().then(
      (userId) => {
        setAnalyticsUser(userId);
        setIsUserLoggedIn(!!userId);
        setLoginInformationId(userId);
      },
      () => {
        setIsUserLoggedIn(false);
        setLoginInformationId(null);
      }
    );
  }, []);

  return (
    <Main
      Component={Component}
      pageProps={pageProps}
      isUserLoggedIn={isUserLoggedIn}
      loginInformationId={loginInformationId}
      queryClient={queryClient}
      locale={locale}
    />
  );
}
